import {useEffect, useRef} from 'react';

type RefetchOnMount = boolean | 'always';

/**
 * `refetchOnMount` option of a query that applies only to the first mount of the component.
 *
 * A page kept alive (`coreUtils/adapters/keepAlive`, React 19 `<Activity>`) runs its effects again when it is
 * shown again: the query observers subscribe again and would refetch stale data, while the page must come
 * back as it was left. React 18 (no `<Activity>`) mounts a component once: same behavior as `refetchOnMount`.
 *
 * Call it before the query hook: its effect runs before the observer subscribes (effects run in order).
 *
 * @param {boolean | 'always'} refetchOnMount - Value for the first mount
 * @returns {Function} - `refetchOnMount` option of the query
 */
const useRefetchOnMount = (refetchOnMount: RefetchOnMount = true): (() => RefetchOnMount) => {
	const mountsRef = useRef(0);

	useEffect(() => {
		mountsRef.current += 1;
	}, []);

	return () => (mountsRef.current > 1 ? false : refetchOnMount);
};

export default useRefetchOnMount;
